import React, { useState, useEffect, useMemo } from 'react';
import { db, auth } from '../firebase';
import { collection, onSnapshot, query, orderBy, doc, updateDoc, deleteDoc, serverTimestamp, Timestamp } from 'firebase/firestore';

const CREATE_URL = 'https://us-central1-core6-marketing.cloudfunctions.net/createKeywordFollowup';
const RUN_CHECK_URL = 'https://us-central1-core6-marketing.cloudfunctions.net/runKeywordFollowupCheck';

const DEFAULT_TAGS = ['title-rewrite', 'internal-linking', 'new-content', 'content-refresh', 'schema', 'gbp', 'ppc'];

// PPC entries skip GSC capture entirely — organic metrics would give false
// verdicts for ad work. They surface at the due date for review in Claude chat.
const entryIsPpc = (e) => e.channel === 'ppc' || (e.actionTags || []).some(t => String(t).toLowerCase() === 'ppc');

const STATUS_META = {
  pending:          { label: 'Pending',          color: 'var(--text-secondary)', bg: 'var(--bg-secondary)' },
  ready_for_review: { label: 'Ready for Review', color: '#818cf8', bg: 'rgba(79,70,229,.12)' },
  reviewed:         { label: 'Reviewed',         color: '#4ade80', bg: 'rgba(22,163,74,.10)' },
  extended:         { label: 'Extended',         color: '#fbbf24', bg: 'rgba(251,191,36,.10)' },
  dismissed:        { label: 'Dismissed',        color: 'var(--text-muted)', bg: 'var(--bg-secondary)' },
};

const FLAG_META = {
  improved: { label: 'Improved', color: '#4ade80' },
  minimal:  { label: 'Minimal',  color: '#fbbf24' },
  declined: { label: 'Declined', color: '#f87171' },
  no_data:  { label: 'No Data',  color: 'var(--text-muted)' },
};

function fmtDate(ts) {
  if (!ts) return '—';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
}

function fmtNum(v, digits = 0) {
  return typeof v === 'number' ? v.toLocaleString('en-US', { maximumFractionDigits: digits, minimumFractionDigits: 0 }) : '—';
}

function fmtPct(v) {
  if (typeof v !== 'number') return '—';
  const pct = Math.round(v * 1000) / 10;
  return `${pct > 0 ? '+' : ''}${pct}%`;
}

function isOverdue(entry) {
  return entry.status === 'pending' && entry.followUpDueAt?.toMillis && entry.followUpDueAt.toMillis() <= Date.now();
}

export default function KeywordFollowups() {
  const [clients, setClients] = useState([]);
  const [entries, setEntries] = useState([]);

  // Log form
  const [formClientId, setFormClientId] = useState('');
  const [keyword, setKeyword] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [notes, setNotes] = useState('');
  const [workDate, setWorkDate] = useState(''); // optional YYYY-MM-DD — backdates the baseline
  const [windowChoice, setWindowChoice] = useState('30'); // '30' | '60' | '90' | 'custom'
  const [customDays, setCustomDays] = useState('45');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [formNotice, setFormNotice] = useState('');

  // List
  const [filterClientId, setFilterClientId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [openId, setOpenId] = useState(null);
  const [busyId, setBusyId] = useState(null); // entry id with an in-flight action
  const [listError, setListError] = useState('');

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'clients'), orderBy('businessName')),
      snap => setClients(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => !c.isArchived)),
      err => console.error('clients snapshot error:', err)
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'keywordFollowups'),
      snap => setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      err => console.error('keywordFollowups snapshot error:', err)
    );
    return unsub;
  }, []);

  // Tag options: defaults + anything ever used on an entry + anything picked this session.
  const tagOptions = useMemo(() => {
    const all = new Set(DEFAULT_TAGS);
    entries.forEach(e => (e.actionTags || []).forEach(t => all.add(t)));
    tags.forEach(t => all.add(t));
    return [...all].sort();
  }, [entries, tags]);

  const clientsById = useMemo(() => Object.fromEntries(clients.map(c => [c.id, c])), [clients]);

  const toggleTag = (t) => setTags(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);

  const addCustomTag = () => {
    const t = tagInput.trim().toLowerCase().replace(/\s+/g, '-');
    if (t && !tags.includes(t)) setTags(prev => [...prev, t]);
    setTagInput('');
  };

  const followUpDays = windowChoice === 'custom'
    ? Math.min(365, Math.max(1, parseInt(customDays, 10) || 30))
    : parseInt(windowChoice, 10);

  const handleLog = async () => {
    setFormError('');
    setFormNotice('');
    if (!formClientId) { setFormError('Pick a client first.'); return; }
    if (!keyword.trim()) { setFormError('Enter the keyword you worked on.'); return; }
    setSaving(true);
    try {
      if (!auth.currentUser) throw new Error('You are signed out — sign in again to log.');
      const idToken = await auth.currentUser.getIdToken();
      const payload = {
        clientId: formClientId,
        keyword: keyword.trim(),
        targetUrl: targetUrl.trim(),
        actionTags: tags,
        notes: notes.trim(),
        workDate: workDate || null,
        followUpDays,
        createdBy: auth.currentUser.email || '',
      };
      const post = (body) => fetch(CREATE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify(body),
      });
      let res = await post(payload);
      let data = await res.json();
      // Duplicate guard: an identical keyword is already pending — confirm intent.
      if (res.status === 409 && data.duplicate) {
        const dueStr = data.duplicate.dueAt ? new Date(data.duplicate.dueAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '?';
        const goAhead = window.confirm(`There's already a pending follow-up for "${data.duplicate.keyword}" on this client (check due ${dueStr}). Log another one anyway?`);
        if (!goAhead) { setFormNotice('Not logged — a pending follow-up for this keyword already exists.'); return; }
        res = await post({ ...payload, allowDuplicate: true });
        data = await res.json();
      }
      if (!res.ok || !data.success) throw new Error(data.error || data.details || 'Failed to log follow-up');
      const b = data.baseline || {};
      const win = b.windowDays || 28;
      const pageNote = b.page && !b.page.noData
        ? ` Target page baseline: ${b.page.clicks} clicks, ${b.page.impressions} impressions.`
        : '';
      setFormNotice(data.channel === 'ppc'
        ? `Logged as a PPC entry — no organic baseline captured (GSC metrics don't reflect ad work). It will surface for review in ${followUpDays} days; pull the ad performance data in Claude chat when it does.`
        : b.noData
          ? `Logged — but GSC returned no baseline data for this keyword (${b.error || `no impressions in the trailing ${win} days`}). The entry was still created; the day-${followUpDays} check will flag it if data appears.${pageNote}`
          : `Logged — baseline captured: position ${b.avgPosition}, ${b.clicks} clicks, ${b.impressions} impressions over the trailing ${win} days.${pageNote}`);
      setKeyword('');
      setTargetUrl('');
      setTags([]);
      setNotes('');
      setWorkDate('');
      setWindowChoice('30');
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // peek=true → "Check Progress": live snapshot, no verdict, no status change,
  // re-runnable anytime. peek=false → "Run Final Check": records the official
  // verdict (Minimal/No-Data verdicts auto-extend the entry).
  const runCheckNow = async (entry, peek = false) => {
    setListError('');
    setBusyId(entry.id);
    try {
      if (!auth.currentUser) throw new Error('You are signed out — sign in again.');
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(RUN_CHECK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ id: entry.id, ...(peek && { peek: true }) }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || data.details || 'Check failed');
    } catch (err) {
      setListError(`Check failed for "${entry.keyword}": ${err.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const setStatus = async (entry, updates) => {
    setListError('');
    setBusyId(entry.id);
    try {
      await updateDoc(doc(db, 'keywordFollowups', entry.id), updates);
    } catch (err) {
      setListError(`Could not update "${entry.keyword}": ${err.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const markReviewed = (entry) => setStatus(entry, { status: 'reviewed', reviewedAt: serverTimestamp() });
  const dismiss = (entry) => setStatus(entry, { status: 'dismissed' });

  // Permanent delete — unlike Dismiss, the doc is gone (baseline included).
  const deleteEntry = async (entry) => {
    const ok = window.confirm(`Permanently delete the follow-up for "${entry.keyword}"${entry.clientName ? ` (${entry.clientName})` : ''}? The baseline snapshot is deleted with it — this can't be undone.`);
    if (!ok) return;
    setListError('');
    setBusyId(entry.id);
    try {
      await deleteDoc(doc(db, 'keywordFollowups', entry.id));
    } catch (err) {
      setListError(`Could not delete "${entry.keyword}": ${err.message}`);
    } finally {
      setBusyId(null);
    }
  };
  // Re-arm the same entry for another 30 days; the original baseline is kept so
  // the eventual comparison still measures total progress since the work.
  const extend30 = (entry) => setStatus(entry, {
    status: 'pending',
    followUpDueAt: Timestamp.fromMillis(Date.now() + 30 * 86400000),
  });

  const visible = entries
    .filter(e => !filterClientId || e.clientId === filterClientId)
    .filter(e => !filterStatus || e.status === filterStatus)
    .sort((a, b) => (a.followUpDueAt?.seconds || 0) - (b.followUpDueAt?.seconds || 0));

  const readyCount = entries.filter(e => e.status === 'ready_for_review').length;

  const selStyle = { background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 7, padding: '8px 12px', fontSize: 13, color: 'var(--text-primary)', outline: 'none' };

  const metricRow = (label, bVal, rVal, delta) => (
    <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 1fr 1fr', gap: 8, padding: '6px 0', borderTop: '1px solid var(--border)', fontSize: 13 }}>
      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{label}</span>
      <span style={{ color: 'var(--text-primary)' }}>{bVal}</span>
      <span style={{ color: 'var(--text-primary)' }}>{rVal}</span>
      <span style={{ color: 'var(--text-secondary)' }}>{delta}</span>
    </div>
  );

  return (
    <div className="page" style={{ maxWidth: 1100 }}>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
          Keyword Follow-Ups
          {readyCount > 0 && (
            <span style={{ fontSize: 12, fontWeight: 700, color: '#818cf8', background: 'rgba(79,70,229,.12)', borderRadius: 20, padding: '3px 10px', marginLeft: 10, verticalAlign: 'middle' }}>
              {readyCount} ready for review
            </span>
          )}
        </h1>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          Log SEO work on a keyword; the Hub snapshots GSC now and re-checks it automatically when the follow-up window ends
        </p>
      </div>

      {/* Log form */}
      <div className="card" style={{ padding: 22, marginBottom: 20, border: '1px solid #4f46e544' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#818cf8', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 14 }}>✦ Log Keyword Work</div>

        {formError && <div className="error-box" style={{ marginBottom: 12, fontSize: 12 }}>{formError}</div>}
        {formNotice && (
          <div style={{ marginBottom: 12, fontSize: 12, color: '#4ade80', background: 'rgba(22,163,74,.08)', border: '1px solid rgba(22,163,74,.3)', borderRadius: 8, padding: '10px 14px', lineHeight: 1.5 }}>
            {formNotice}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label>Client</label>
            <select value={formClientId} onChange={e => setFormClientId(e.target.value)} disabled={saving} style={{ ...selStyle, width: '100%' }}>
              <option value="">— Choose a client —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.businessName}</option>)}
            </select>
          </div>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label>Keyword</label>
            <input type="text" placeholder="e.g. emergency water heater repair" value={keyword} onChange={e => setKeyword(e.target.value)} disabled={saving} style={{ fontSize: 13 }} />
          </div>
        </div>

        <div className="form-field" style={{ marginTop: 14 }}>
          <label>Target URL <span style={{ color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— the page the work was done on</span></label>
          <input type="text" placeholder="https://client.com/water-heater-repair" value={targetUrl} onChange={e => setTargetUrl(e.target.value)} disabled={saving} style={{ fontSize: 13 }} />
        </div>

        <div className="form-field">
          <label>Action Tags</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            {tagOptions.map(t => {
              const on = tags.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleTag(t)}
                  disabled={saving}
                  style={{
                    fontSize: 12, borderRadius: 20, padding: '4px 12px', cursor: 'pointer',
                    border: `1px solid ${on ? '#4f46e5' : 'var(--border)'}`,
                    background: on ? 'rgba(79,70,229,.15)' : 'var(--bg-primary)',
                    color: on ? '#818cf8' : 'var(--text-secondary)',
                  }}
                >
                  {t}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              placeholder="Add a new tag…"
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomTag(); } }}
              disabled={saving}
              style={{ fontSize: 12, maxWidth: 220 }}
            />
            <button type="button" className="btn btn-sm btn-secondary" onClick={addCustomTag} disabled={saving || !tagInput.trim()}>Add</button>
          </div>
          {tags.some(t => t.toLowerCase() === 'ppc') && (
            <div style={{ fontSize: 12, color: '#38bdf8', marginTop: 8, lineHeight: 1.5 }}>
              ⓘ PPC entry — the Hub won't capture organic GSC metrics (they'd give a false verdict for ad work). It will surface at the due date for review; pull the ad data in Claude chat.
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label>Follow-Up Window</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select value={windowChoice} onChange={e => setWindowChoice(e.target.value)} disabled={saving} style={selStyle}>
                <option value="30">30 days</option>
                <option value="60">60 days</option>
                <option value="90">90 days</option>
                <option value="custom">Custom…</option>
              </select>
              {windowChoice === 'custom' && (
                <input type="number" min="1" max="365" value={customDays} onChange={e => setCustomDays(e.target.value)} disabled={saving} style={{ fontSize: 13, width: 90 }} />
              )}
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                checks {new Date((workDate ? new Date(workDate + 'T00:00:00').getTime() : Date.now()) + followUpDays * 86400000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            </div>
          </div>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label>Work Date <span style={{ color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— when the work was done, if not today</span></label>
            <input
              type="date"
              value={workDate}
              max={new Date().toISOString().slice(0, 10)}
              onChange={e => setWorkDate(e.target.value)}
              disabled={saving}
              style={{ ...selStyle, width: '100%' }}
            />
          </div>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label>Notes <span style={{ color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— optional</span></label>
            <input type="text" placeholder="What exactly was changed?" value={notes} onChange={e => setNotes(e.target.value)} disabled={saving} style={{ fontSize: 13 }} />
          </div>
        </div>

        <button
          className="btn btn-primary"
          style={{ width: '100%', marginTop: 18, background: '#4f46e5', borderColor: '#4f46e5' }}
          onClick={handleLog}
          disabled={saving}
        >
          {saving ? (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <span className="spinner" style={{ width: 14, height: 14 }}></span> Capturing baseline from Search Console…
            </span>
          ) : '✦ Log Follow-Up'}
        </button>
      </div>

      {/* Filters */}
      <div className="card" style={{ padding: '14px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.4px' }}>Filter</span>
        <select value={filterClientId} onChange={e => setFilterClientId(e.target.value)} style={selStyle}>
          <option value="">All clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.businessName}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selStyle}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
        <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>{visible.length} of {entries.length} entries</span>
      </div>

      {listError && <div className="error-box" style={{ marginBottom: 12, fontSize: 12 }}>{listError}</div>}

      {/* List */}
      {visible.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
          <div style={{ fontSize: 14 }}>{entries.length === 0 ? 'No follow-ups logged yet — log the first one above.' : 'Nothing matches these filters.'}</div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 820 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1.4fr 110px 110px 140px 100px', gap: 8, padding: '12px 20px', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                <span>Client</span><span>Keyword</span><span>Logged</span><span>Due</span><span>Status</span><span>Flag</span>
              </div>

              {visible.map((e, idx) => {
                const isOpen = openId === e.id;
                const sm = STATUS_META[e.status] || STATUS_META.pending;
                const ppc = entryIsPpc(e);
                const fm = ppc
                  ? (e.result ? { label: 'PPC Review', color: '#38bdf8' } : { label: 'PPC', color: '#38bdf8' })
                  : (e.result?.flag ? FLAG_META[e.result.flag] : null);
                const overdue = isOverdue(e);
                const busy = busyId === e.id;
                const b = e.baseline || {};
                // The grid shows the newest snapshot: an interim "Check
                // Progress" peek when it's fresher than the official result.
                // The flag chip and status stay driven by the official result.
                const official = e.result;
                const peekSnap = e.lastPeek;
                const r = peekSnap && (!official || (peekSnap.capturedAt?.seconds || 0) > (official.capturedAt?.seconds || 0))
                  ? peekSnap
                  : official;
                const isInterim = !!r && r !== official;
                return (
                  <div key={e.id} style={{ borderTop: idx === 0 ? 'none' : '1px solid var(--border)' }}>
                    <div
                      onClick={() => setOpenId(isOpen ? null : e.id)}
                      style={{ display: 'grid', gridTemplateColumns: '1.1fr 1.4fr 110px 110px 140px 100px', gap: 8, alignItems: 'center', padding: '10px 20px', cursor: 'pointer', background: isOpen ? 'var(--bg-primary)' : 'transparent' }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.clientName || '—'}</span>
                      <span style={{ fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {e.keyword}
                        {e.extendedFromId && <span title="Auto re-check of an earlier entry" style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>↻</span>}
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{fmtDate(e.loggedAt)}</span>
                      <span style={{ fontSize: 12, color: overdue ? '#f87171' : 'var(--text-secondary)', fontWeight: overdue ? 600 : 400 }}>{fmtDate(e.followUpDueAt)}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, color: sm.color, background: sm.bg, borderRadius: 20, padding: '3px 10px', textAlign: 'center', whiteSpace: 'nowrap' }}>{sm.label}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: fm ? fm.color : 'var(--text-muted)' }}>{fm ? fm.label : '—'}</span>
                    </div>

                    {isOpen && (
                      <div style={{ padding: '4px 20px 18px', background: 'var(--bg-primary)' }}>
                        {/* Meta line */}
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10, display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                          {e.targetUrl && <span>🔗 <a href={e.targetUrl} target="_blank" rel="noreferrer" style={{ color: '#818cf8' }}>{e.targetUrl.replace(/https?:\/\//, '')}</a></span>}
                          <span>{e.followUpDays}-day window</span>
                          {e.workDate && <span>work done {e.workDate}</span>}
                          {(e.autoExtendCount || 0) > 0 && <span>auto re-check #{e.autoExtendCount}{e.autoExtendCount >= 2 ? ' (final)' : ''}</span>}
                          {e.createdBy && <span>logged by {e.createdBy}</span>}
                          {e.reviewedAt && <span>reviewed {fmtDate(e.reviewedAt)}</span>}
                        </div>

                        {(e.actionTags || []).length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                            {e.actionTags.map(t => (
                              <span key={t} style={{ fontSize: 11, color: 'var(--text-secondary)', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 20, padding: '2px 10px' }}>{t}</span>
                            ))}
                          </div>
                        )}
                        {e.notes && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12, lineHeight: 1.5 }}>{e.notes}</div>}

                        {/* Baseline vs result — PPC entries have no GSC snapshots by design */}
                        {ppc ? (
                        <div style={{ background: 'rgba(56,189,248,.06)', border: '1px solid rgba(56,189,248,.3)', borderRadius: 8, padding: '14px 16px', marginBottom: 12, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                          <span style={{ fontWeight: 700, color: '#38bdf8' }}>PPC entry</span> — organic Search Console metrics are intentionally not captured (they don't reflect ad performance and would give a false verdict), so there are no numbers to show here.
                          {e.result
                            ? ` Surfaced for review ${fmtDate(e.result.capturedAt)} — review the ad performance in Claude chat: ask about this follow-up and have Claude pull the Google Ads data alongside it, then Mark Reviewed here.`
                            : ` It will surface for review on ${fmtDate(e.followUpDueAt)} — or click "Surface for Review Now" below to review it in Claude chat today.`}
                        </div>
                        ) : (
                        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 1fr 1fr', gap: 8, paddingBottom: 6, fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                            <span></span>
                            <span>Baseline · {fmtDate(b.capturedAt)}</span>
                            <span style={isInterim ? { color: '#38bdf8' } : undefined}>
                              {isInterim ? 'Progress' : 'Result'} · {r ? fmtDate(r.capturedAt) : 'not run yet'}
                            </span>
                            <span>Change</span>
                          </div>
                          {metricRow('Avg Position', fmtNum(b.avgPosition, 1), r ? fmtNum(r.avgPosition, 1) : '—',
                            r && typeof r.positionDelta === 'number'
                              ? <span style={{ color: r.positionDelta > 0 ? '#4ade80' : r.positionDelta < 0 ? '#f87171' : undefined, fontWeight: 600 }}>{r.positionDelta > 0 ? `↑ ${r.positionDelta}` : r.positionDelta < 0 ? `↓ ${Math.abs(r.positionDelta)}` : '0'}</span>
                              : '—')}
                          {metricRow('Clicks', fmtNum(b.clicks), r ? fmtNum(r.clicks) : '—', r ? fmtPct(r.clicksPctChange) : '—')}
                          {metricRow('Impressions', fmtNum(b.impressions), r ? fmtNum(r.impressions) : '—', r ? fmtPct(r.impressionsPctChange) : '—')}
                          {metricRow('CTR', typeof b.ctr === 'number' ? `${b.ctr}%` : '—', r && typeof r.ctr === 'number' ? `${r.ctr}%` : '—', '')}

                          {/* Target-page aggregate — all queries landing on the URL, not just this keyword */}
                          {(b.page || r?.page) && (
                            <>
                              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginTop: 10, paddingBottom: 4 }}>
                                Target page — all queries
                              </div>
                              {metricRow('Avg Position', fmtNum(b.page?.avgPosition, 1), r ? fmtNum(r.page?.avgPosition, 1) : '—',
                                r && typeof r.pagePositionDelta === 'number'
                                  ? <span style={{ color: r.pagePositionDelta > 0 ? '#4ade80' : r.pagePositionDelta < 0 ? '#f87171' : undefined, fontWeight: 600 }}>{r.pagePositionDelta > 0 ? `↑ ${r.pagePositionDelta}` : r.pagePositionDelta < 0 ? `↓ ${Math.abs(r.pagePositionDelta)}` : '0'}</span>
                                  : '—')}
                              {metricRow('Clicks', fmtNum(b.page?.clicks), r ? fmtNum(r.page?.clicks) : '—', r ? fmtPct(r.pageClicksPctChange) : '—')}
                              {metricRow('Impressions', fmtNum(b.page?.impressions), r ? fmtNum(r.page?.impressions) : '—', r ? fmtPct(r.pageImpressionsPctChange) : '—')}
                            </>
                          )}

                          {/* Local pack (Google Maps) rank — only present when the client had
                              local tracking configured at the time each snapshot was taken */}
                          {(b.local || r?.local) && (
                            <>
                              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginTop: 10, paddingBottom: 4 }}>
                                Local Pack (Google Maps)
                              </div>
                              {metricRow('Maps Rank',
                                b.local ? (b.local.error ? '—' : b.local.found ? `#${b.local.rank}` : `not in top ${b.local.checkedDepth || '?'}`) : '—',
                                r?.local ? (r.local.error ? '—' : r.local.found ? `#${r.local.rank}` : `not in top ${r.local.checkedDepth || '?'}`) : (r ? '—' : 'not run yet'),
                                r && typeof r.localRankDelta === 'number'
                                  ? <span style={{ color: r.localRankDelta > 0 ? '#4ade80' : r.localRankDelta < 0 ? '#f87171' : undefined, fontWeight: 600 }}>{r.localRankDelta > 0 ? `↑ ${r.localRankDelta}` : r.localRankDelta < 0 ? `↓ ${Math.abs(r.localRankDelta)}` : '0'}</span>
                                  : '—')}
                              {b.local?.error && <div style={{ fontSize: 11, color: '#fbbf24', marginTop: 4 }}>⚠ Baseline Maps check: {b.local.error}</div>}
                              {r?.local?.error && <div style={{ fontSize: 11, color: '#fbbf24', marginTop: 4 }}>⚠ Result Maps check: {r.local.error}</div>}
                            </>
                          )}
                          {!b.local && !r?.local && clientsById[e.clientId]?.localPlaceId && clientsById[e.clientId]?.localScanPoint && (
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 10 }}>
                              Local Pack tracking was set up for this client after this entry was logged — it won't apply retroactively, but future follow-ups will include it.
                            </div>
                          )}

                          {(b.noData || b.error) && (
                            <div style={{ fontSize: 12, color: '#fbbf24', marginTop: 8 }}>
                              ⚠ Baseline: {b.error || `GSC had no data for this keyword in the trailing ${b.windowDays || 28} days`}
                            </div>
                          )}
                          {r && r.flag === 'no_data' && (
                            <div style={{ fontSize: 12, color: '#fbbf24', marginTop: 8 }}>
                              ⚠ Result: {r.error || 'GSC returned no data at check time'} — check tracking before assuming nothing happened.
                            </div>
                          )}
                          {(b.monthly || r?.monthly) && (
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.5 }}>
                              Monthly rankings snapshot (90-day avg):
                              {b.monthly && ` at log time (${b.monthly.month}) pos ${b.monthly.position}, ${b.monthly.clicks} clicks`}
                              {b.monthly && r?.monthly && ' · '}
                              {r?.monthly && ` at check time (${r.monthly.month}) pos ${r.monthly.position}, ${r.monthly.clicks} clicks`}
                            </div>
                          )}
                          {isInterim && (
                            <div style={{ fontSize: 12, color: '#38bdf8', marginTop: 8, lineHeight: 1.5 }}>
                              👁 Interim progress check — no verdict recorded, nothing changed on this entry.
                              {official
                                ? ` The official verdict from ${fmtDate(official.capturedAt)} still stands.`
                                : ` The official check runs ${fmtDate(e.followUpDueAt)}.`}
                            </div>
                          )}
                        </div>
                        )}

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {!ppc && (e.status === 'pending' || e.status === 'ready_for_review' || e.status === 'extended') && (
                            <button
                              className="btn btn-sm btn-secondary"
                              style={{ color: '#38bdf8', borderColor: 'rgba(56,189,248,.4)' }}
                              disabled={busy}
                              title="Live snapshot vs the baseline — run as often as you like; records no verdict and changes nothing on the entry"
                              onClick={() => runCheckNow(e, true)}
                            >
                              {busy ? 'Checking…' : '👁 Check Progress'}
                            </button>
                          )}
                          {(e.status === 'pending' || (!ppc && e.status === 'ready_for_review')) && (
                            <button
                              className="btn btn-sm btn-secondary"
                              disabled={busy}
                              title={ppc
                                ? 'Surfaces this PPC entry for its chat review now instead of waiting for the due date'
                                : 'Records the official verdict now instead of waiting for the due date — a Minimal or No-Data verdict auto-extends the follow-up'}
                              onClick={() => runCheckNow(e)}
                            >
                              {busy ? 'Checking…' : ppc ? '⟳ Surface for Review Now' : '⟳ Run Final Check'}
                            </button>
                          )}
                          {(e.status === 'ready_for_review' || e.status === 'extended') && (
                            <button className="btn btn-sm btn-primary" style={{ background: '#16a34a', borderColor: '#16a34a' }} disabled={busy} onClick={() => markReviewed(e)}>✓ Mark Reviewed</button>
                          )}
                          {/* No Extend on `extended` entries — the auto-created re-check already IS
                              the extension; re-arming this one too would double the chain. */}
                          {e.status === 'ready_for_review' && (
                            <button className="btn btn-sm btn-secondary" disabled={busy} onClick={() => extend30(e)}>Extend 30 More Days</button>
                          )}
                          {e.status !== 'dismissed' && e.status !== 'reviewed' && (
                            <button className="btn btn-sm btn-secondary" style={{ color: 'var(--text-muted)' }} disabled={busy} onClick={() => dismiss(e)}>Dismiss</button>
                          )}
                          <button className="btn btn-sm btn-secondary" style={{ color: '#f87171', marginLeft: 'auto' }} disabled={busy} onClick={() => deleteEntry(e)}>🗑 Delete</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12, lineHeight: 1.6 }}>
        Baselines and results are trailing-28-day Search Console averages for the exact keyword (site-wide), captured live. When a target URL is set, the page's aggregate metrics across all queries are captured too — a clear page-level win counts as improvement even if the exact keyword looks flat. Clients with Local Pack Tracking configured (Content Data tab) also get a Google Maps rank snapshot, independent of Search Console — a clear Maps win counts too. Set a work date when logging after the fact so the baseline ends before the work. <strong>Check Progress</strong> takes a live no-verdict snapshot you can run anytime on an open entry; <strong>Run Final Check</strong> records the official verdict early instead of waiting for the due date. Entries flagged Minimal or No Data are auto-extended with a 30-day re-check against the original baseline, at most twice — then they surface for review. Tag an entry <strong>ppc</strong> to skip organic capture entirely — it surfaces at the due date for an ad-data review in chat instead. Ask Claude "what keyword follow-ups are due?" to review these in chat.
      </div>
    </div>
  );
}
